import { useState } from 'react';
import { toBoolOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import type {
    ILabOrdersFormValues,
    ILabOrdersModel,
    ILabOrdersSubmitResult,
    IPwlLabStartResult,
    TPwlStartAction,
} from '@/types/history/lab-orders';

const REGISTER_ENDPOINT = '/api/register';

const LAB_ORDERS_ENDPOINT = REGISTER_ENDPOINT + '/lab-orders';

const defaultLabOrdersModel: ILabOrdersModel = {
    id: 0,
    editKey: '',
    isIE: false,

    fullName: '',
    dateOfBirth: '',
    plDashPhoneNumber: '',

    labOrdersFailed: false,
    labOrdersPending: false,
    requisitionNumber: '',
    hasLabRequisition: false,
    hasJugStatus: false,
    isLiver: false,

    isNextStepCenterSelect: false,
    isPhlebot: false,
    canChoosePhlebot: false,
    hasPreferredCenter: false,

    distanceToQuest: 0,

    languageData: {},
};

interface IPwlLabStartResults {
    redirectUrl: string | null;
    labOrders: Partial<ILabOrdersModel> | null;
}

type TStartLabsResult = IPwlLabStartResult & { labOrders?: Partial<ILabOrdersModel> | null };

export const useLabOrders = (registrationId: number, editKey: string) => {
    const url = `${LAB_ORDERS_ENDPOINT}/${registrationId}/${editKey}`;

    const { model: loaded, isLoading, error } = useApiModel(url, defaultLabOrdersModel);
    const [override, setOverride] = useState<ILabOrdersModel | null>(null);
    const model = override ?? loaded;

    const labOrders = useApiSubmit<ILabOrdersFormValues, { startAction: TPwlStartAction }, ILabOrdersSubmitResult>(
        url,
        (values) => ({
            labChoice: values.labChoice,
            connectToDonorIsDesired: toBoolOrNull(values.connectToDonorIsDesired),
        }),
        (results) => ({ startAction: results?.startAction }),
    );

    const toStartResult = (results: IPwlLabStartResults) => ({
        redirectUrl: results?.redirectUrl,
        labOrders: results?.labOrders,
    });

    const startLabsFailureMessage = 'We were not able to start your labs. Please try again.';

    const questStart = useApiSubmit<void, IPwlLabStartResults, TStartLabsResult>(
        `${REGISTER_ENDPOINT}/pwl-lab-start-quest/${registrationId}/${editKey}`,
        () => ({}),
        toStartResult,
        startLabsFailureMessage,
    );

    const phlebotStart = useApiSubmit<void, IPwlLabStartResults, TStartLabsResult>(
        `${REGISTER_ENDPOINT}/pwl-lab-start-phlebot/${registrationId}/${editKey}`,
        () => ({}),
        toStartResult,
        startLabsFailureMessage,
    );

    /**
     * Runs the pre-workup lab orchestration for the chosen option. Refreshes the model in
     * place, unless the server hands back a page that hasn't been migrated yet.
     */
    const startLabs = (action: TPwlStartAction): Promise<IPwlLabStartResult> =>
        (action === 'pwl-lab-start-quest' ? questStart : phlebotStart).submit().then(({ labOrders: refreshed, ...result }) => {
            if (refreshed) setOverride({ ...defaultLabOrdersModel, ...refreshed });

            return result;
        });

    return {
        model,
        isLoading,
        error,
        submit: labOrders.submit,
        isSubmitting: labOrders.isSubmitting,
        submitError: labOrders.submitError ?? questStart.submitError ?? phlebotStart.submitError,
        startLabs,
        isStartingLabs: questStart.isSubmitting || phlebotStart.isSubmitting,
    };
};
